import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';

const MAX_W = 360; // on-screen crop frame, px
const MAX_H = 280;
const MAX_ZOOM = 4;
const MAX_OUTPUT = 1600; // px wide, at most
export const WIDTHS = [25, 50, 75, 100]; // % of the text column an embedded image can take

const ASPECTS = [
  { key: 'orig', label: 'Original' },
  { key: '1:1', label: '1:1' },
  { key: '4:3', label: '4:3' },
  { key: '3:2', label: '3:2' },
  { key: '16:9', label: '16:9' },
  { key: '3:4', label: '3:4' },
];

// Crops a picked JPEG/PNG (position + zoom inside a frame of the chosen shape) and picks how wide it
// sits in the text. Calls onSave(blob, { width }) with the cropped image (PNG stays PNG, otherwise
// JPEG), or onCancel().
const ImageCropper = ({ file, saving, onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const [image, setImage] = useState(null);
  const [failed, setFailed] = useState(false);
  const [aspectKey, setAspectKey] = useState('orig');
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // image centre relative to the frame centre
  const [width, setWidth] = useState(100);

  const isPng = file.type === 'image/png';

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => setImage(img);
    img.onerror = () => setFailed(true);
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  let aspect = 1;
  if (image) {
    if (aspectKey === 'orig') aspect = image.width / image.height;
    else { const [a, b] = aspectKey.split(':').map(Number); aspect = a / b; }
  }
  const frameW = aspect >= MAX_W / MAX_H ? MAX_W : Math.round(MAX_H * aspect);
  const frameH = aspect >= MAX_W / MAX_H ? Math.round(MAX_W / aspect) : MAX_H;

  // Scale at zoom 1: the image just covers the frame.
  const baseScale = image ? Math.max(frameW / image.width, frameH / image.height) : 1;

  // Keeps the image covering the whole frame.
  const clamp = (pos, nextZoom) => {
    if (!image) return pos;
    const scale = baseScale * nextZoom;
    const maxX = Math.max(0, (image.width * scale - frameW) / 2);
    const maxY = Math.max(0, (image.height * scale - frameH) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, pos.x)), y: Math.min(maxY, Math.max(-maxY, pos.y)) };
  };

  const draw = (canvas, outW, transparent) => {
    const factor = outW / frameW;
    const outH = Math.round(frameH * factor);
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!transparent) {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, outW, outH);
    }
    const scale = baseScale * zoom * factor;
    const w = image.width * scale;
    const h = image.height * scale;
    ctx.drawImage(image, outW / 2 + offset.x * factor - w / 2, outH / 2 + offset.y * factor - h / 2, w, h);
  };

  useEffect(() => {
    if (image && canvasRef.current) draw(canvasRef.current, frameW, false);
  });

  const changeAspect = (key) => {
    setAspectKey(key);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  const onPointerDown = (e) => {
    dragRef.current = { x: e.clientX, y: e.clientY, start: offset };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    setOffset(clamp({ x: drag.start.x + e.clientX - drag.x, y: drag.start.y + e.clientY - drag.y }, zoom));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const changeZoom = (e) => {
    const next = parseFloat(e.target.value);
    setZoom(next);
    setOffset((pos) => clamp(pos, next));
  };

  const save = () => {
    // Source pixels the frame covers, capped so uploads stay small (never scaled up).
    const outW = Math.max(1, Math.min(MAX_OUTPUT, Math.round(frameW / (baseScale * zoom))));
    const out = document.createElement('canvas');
    draw(out, outW, isPng);
    out.toBlob((blob) => blob && onSave(blob, { width }), isPng ? 'image/png' : 'image/jpeg', 0.9);
  };

  return (
    <Modal show onHide={saving ? undefined : onCancel} centered>
      <Modal.Header closeButton={!saving}><Modal.Title>Crop &amp; size photo</Modal.Title></Modal.Header>
      <Modal.Body className="text-center">
        {failed && <p className="text-danger">Couldn't read that image. Try a JPG or PNG.</p>}
        {!failed && !image && <Spinner animation="border" />}
        {image && (
          <React.Fragment>
            <div className="d-flex justify-content-center flex-wrap mb-2" style={{ gap: '4px' }}>
              {ASPECTS.map(a => (
                <Button key={a.key} size="sm" variant={a.key === aspectKey ? 'primary' : 'outline-secondary'}
                  disabled={saving} onClick={() => changeAspect(a.key)}>{a.label}</Button>
              ))}
            </div>
            <div style={{ width: MAX_W, height: MAX_H, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <canvas
                ref={canvasRef}
                aria-label="Drag to position the photo"
                style={{ display: 'block', width: frameW, height: frameH, cursor: 'grab', touchAction: 'none', background: '#fff' }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              />
            </div>
            <Form.Group className="mt-3 mb-0" controlId="crop-zoom">
              <Form.Label className="small text-muted">Drag to move, slide to zoom</Form.Label>
              <Form.Control type="range" min="1" max={MAX_ZOOM} step="0.01" value={zoom} onChange={changeZoom} disabled={saving} />
            </Form.Group>

            <div className="small text-muted mt-3 mb-1">Width in the text</div>
            <div className="d-flex justify-content-center mb-2" style={{ gap: '4px' }}>
              {WIDTHS.map(w => (
                <Button key={w} size="sm" variant={w === width ? 'primary' : 'outline-secondary'}
                  disabled={saving} onClick={() => setWidth(w)}>{w}%</Button>
              ))}
            </div>
            {/* How it will sit next to the text. */}
            <div style={{ width: MAX_W, margin: '0 auto', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', textAlign: 'left' }}>
              <div style={{ height: 6, margin: '4px 0', background: '#ddd', borderRadius: 3 }} />
              <div style={{ height: 6, margin: '4px 0', background: '#ddd', borderRadius: 3, width: '85%' }} />
              <div style={{ width: `${width}%`, aspectRatio: `${frameW} / ${frameH}`, margin: '8px 0', background: '#9aa4ad', borderRadius: 4 }} />
              <div style={{ height: 6, margin: '4px 0', background: '#ddd', borderRadius: 3 }} />
              <div style={{ height: 6, margin: '4px 0', background: '#ddd', borderRadius: 3, width: '60%' }} />
            </div>
          </React.Fragment>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={saving} onClick={onCancel}>Skip</Button>
        <Button disabled={!image || saving} onClick={save}>{saving ? 'Uploading…' : 'Insert'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImageCropper;
